// What a pipeline's stages take, read from its definition: the pipeline's
// `operation_types`, its named `routes`, and each stage's `route_id`. Shared
// by the Studio's lint and by the graphs that show a stage's rule.
//
// Operation values are free text: custom values and the empty string are
// legal and are compared as written. `null` means "not set", as it does to
// the engine.

export type Obj = Record<string, unknown>;

export const isObject = (value: unknown): value is Obj =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** The one attribute a route may match on, outside a windowed pipeline's
 *  results. */
export const ROUTED_ATTRIBUTE = "operation_type";

/** Why a list of values is not one, or null when it is: a non-empty array
 *  of strings. `noun` names what the list holds. */
function listProblem(value: unknown, noun: string): string | null {
  if (!Array.isArray(value)) return "must be an array of strings";
  if (value.length === 0) return `must list at least one ${noun}`;
  if (value.some((item) => typeof item !== "string")) {
    return "must contain only strings";
  }
  return null;
}

/** Why a list of operations is not one, or null when it is. */
export function operationsProblem(value: unknown): string | null {
  return listProblem(value, "operation");
}

/** Why the values a route takes for one attribute are not a list of them,
 *  or null when they are. */
export function valuesProblem(value: unknown): string | null {
  return listProblem(value, "value");
}

/** Whether the pipeline cuts its records into tumbling windows. Its
 *  results carry their group's values and the attributes later middlewares
 *  set, so a route only its sinks name may match on any of them. */
export function isWindowed(doc: Obj): boolean {
  return isObject(doc.batch) && doc.batch.kind === "tumbling";
}

/** The one route defined under a non-blank `id`; none when it is missing
 *  or defined twice. */
function uniqueRoute(doc: Obj, id: string): Obj | null {
  if (!Array.isArray(doc.routes) || id.trim() === "") return null;
  const named = doc.routes.filter((r) => isObject(r) && r.id === id);
  return named.length === 1 ? (named[0] as Obj) : null;
}

/** The operations a route's attributes name, when `operation_type` is
 *  their only key and its list is well formed. */
function attributeOperations(attributes: unknown): string[] | null {
  if (!isObject(attributes)) return null;
  const keys = Object.keys(attributes);
  if (keys.length !== 1 || keys[0] !== ROUTED_ATTRIBUTE) return null;
  const operations = attributes[ROUTED_ATTRIBUTE];
  return operationsProblem(operations) ? null : (operations as string[]);
}

/** What a stage takes, as its own rule says. Never an end-to-end answer:
 *  sinks may overlap, and a script can rewrite the operation on the way. */
export type StageOperations =
  /** No rule: the stage takes every record. */
  | { kind: "all" }
  | { kind: "only"; operations: string[] }
  /** A windowed pipeline's later stage, routed by the attributes its window
   *  results carry, in the order the route writes them. */
  | { kind: "attributes"; attributes: [string, string[]][] }
  /** A rule is written but cannot be read (undefined route, malformed list).
   *  Not the same as "all", and never to be shown as it. */
  | { kind: "invalid" };

const ALL: StageOperations = { kind: "all" };
const INVALID: StageOperations = { kind: "invalid" };

/** How a stage's route reads: by operation, or, on a windowed pipeline's
 *  sink, by any attribute the window results carry. The engine routes no
 *  other stage of a windowed pipeline by attribute. */
type StageSide = "operations" | "windowResults";

function ruleNamedBy(
  doc: Obj,
  stage: unknown,
  side: StageSide,
): StageOperations {
  if (!isObject(stage)) return ALL;
  const ref = stage.route_id;
  if (ref === undefined || ref === null) return ALL;
  if (typeof ref !== "string") return INVALID;
  const route = uniqueRoute(doc, ref);
  return route ? routeRule(doc, route, side) : INVALID;
}

/** What one well-named route says a stage takes. */
function routeRule(doc: Obj, route: Obj, side: StageSide): StageOperations {
  const operations = attributeOperations(route.attributes);
  if (operations) return { kind: "only", operations };
  const byAttributes = side === "windowResults" && isWindowed(doc);
  const attributes = byAttributes ? routedAttributes(route.attributes) : null;
  return attributes ? { kind: "attributes", attributes } : INVALID;
}

/** A route's attributes when every name is non-empty and every list is
 *  well formed: what a windowed pipeline's later stage may route on. */
function routedAttributes(attributes: unknown): [string, string[]][] | null {
  if (!isObject(attributes)) return null;
  const entries = Object.entries(attributes);
  const readable = entries.every(
    ([key, values]) => key !== "" && !valuesProblem(values),
  );
  return entries.length > 0 && readable
    ? (entries as [string, string[]][])
    : null;
}

/** What the source takes: the pipeline's `operation_types`, or the route the
 *  source names. */
export function sourceOperations(doc: unknown): StageOperations {
  if (!isObject(doc)) return ALL;
  const listed = doc.operation_types;
  if (listed === undefined || listed === null) {
    return ruleNamedBy(doc, doc.source, "operations");
  }
  return listedOperations(listed, doc.source);
}

/** The pipeline's `operation_types` as the source's rule. Written together
 *  with a route on the source, the engine refuses both, so neither is it. */
function listedOperations(listed: unknown, source: unknown): StageOperations {
  if (isObject(source) && source.route_id != null) return INVALID;
  return operationsProblem(listed)
    ? INVALID
    : { kind: "only", operations: listed as string[] };
}

/** What a middleware or sink takes: the route it names, nothing inferred. */
export function stageOperations(
  doc: unknown,
  section: "middlewares" | "sinks",
  index: number,
): StageOperations {
  if (!isObject(doc)) return ALL;
  const list = doc[section];
  const stage = Array.isArray(list) ? list[index] : undefined;
  const side = section === "sinks" ? "windowResults" : "operations";
  return ruleNamedBy(doc, stage, side);
}

/** Whether a document chooses operations anywhere: at the source or at any
 *  stage. */
export function usesRouting(doc: unknown): boolean {
  if (!isObject(doc)) return false;
  if (sourceOperations(doc).kind !== "all") return true;
  return (["middlewares", "sinks"] as const).some((section) => {
    const list = doc[section];
    return (
      Array.isArray(list) &&
      list.some((_, i) => stageOperations(doc, section, i).kind !== "all")
    );
  });
}

/** Whether pipeline code cuts its records into tumbling windows. Code that
 *  does not parse does not: its errors are the linter's to show. */
export function codeIsWindowed(code: string): boolean {
  try {
    const doc: unknown = JSON.parse(code);
    return isObject(doc) && isWindowed(doc);
  } catch {
    return false;
  }
}

/** Whether pipeline code chooses operations anywhere. Code that does not
 *  parse chooses nothing: its errors are the linter's to show. */
export function codeUsesRouting(code: string): boolean {
  try {
    return usesRouting(JSON.parse(code));
  } catch {
    return false;
  }
}

/** The operations as a card shows them. An empty value is a real value, so
 *  it is shown as one. */
export function operationsText(operations: string[]): string {
  return operations.map((o) => (o === "" ? '""' : o)).join(", ");
}

/** A route by attributes as a card shows it: `key: a, b; other: c`. */
export function attributesText(attributes: [string, string[]][]): string {
  return attributes
    .map(([key, values]) => `${key}: ${operationsText(values)}`)
    .join("; ");
}
